import React, { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { useGetEvent, useCreateMoments } from 'legacy/events/hooks';
import { FiArrowLeft } from 'react-icons/fi';
import classNames from 'classnames';
import { useGetMe } from 'legacy/user/hooks';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { useImageUploadCommand } from 'legacy/common/hooks/useImageUploadCommand';
import { showError } from 'legacy/common/utils/toast';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';
import FileUploader from 'legacy/events/components/upload-moments/FileUploader';
import UploadedMomentsEmptyState from 'legacy/events/components/upload-moments/UploadedMomentsEmptyState';
import UploadedMomentsList from 'legacy/events/components/upload-moments/UploadedMomentsList';
import { isValidImage } from 'legacy/common/utils/image';
import styles from './style.module.scss';

const UPLOAD_CHUNK_SIZE = 50;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const MAX_IMAGE_SIZE = 100 * 1024 * 1024; // 100MB
const standardDescription = `Hosts and photographers can bulk upload event images here. For
              viewing, captioning, and basic editing, please use the Bounce
              mobile app`;

const Header = ({
  title,
  hasUser,
  onBack
}: {
  title: string;
  hasUser: boolean;
  onBack: () => void;
}) => (
  <div
    className={classNames(
      'fixed h-14 md:h-16 top-0 lg:top-[50px] left-0 w-full px-4 py-2 md:py-6 bg-white shadow-sm z-50 flex items-center',
      {
        'md:top-[80px]': hasUser
      }
    )}
  >
    <div className="w-full flex items-center justify-between  md:w-[800px] md:mx-auto">
      <div className="flex items-center">
        <button
          aria-label="back"
          type="button"
          className="mr-2"
          onClick={onBack}
        >
          <FiArrowLeft size={24} className="text-gray-400" />
        </button>

        <div className="md:flex md:items-center">
          <h1 className="text-black text-base md:text-xl md:font-semibold md:mr-4">
            {title}
          </h1>
        </div>
      </div>
    </div>
  </div>
);
type UploadMomentsProps = {
  eventId?: string;
  albumId?: string;
  hideHeader?: boolean;
  description?: string;
  goBack?: () => void;
};

const UploadMoments = ({
  eventId,
  albumId,
  hideHeader,
  description = standardDescription,
  goBack
}: UploadMomentsProps) => {
  const router = useRouter();
  const { t } = useTranslation('organizations');

  const { data: event, isLoading: isEventLoading } = eventId
    ? useGetEvent(eventId)
    : { data: null, isLoading: false };

  const { data: user, isLoading: isUserLoading } = useGetMe();
  const [uploadedMoments, setUploadedMoments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: uploadImage } = useImageUploadCommand();
  const { mutateAsync: createMoments } = useCreateMoments();

  const handleOnDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setIsUploading(true);

      const isValidImages = await Promise.all(
        acceptedFiles.map((file) =>
          isValidImage(file, {
            allowedTypes: ACCEPTED_IMAGE_TYPES,
            maxSize: MAX_IMAGE_SIZE
          })
        )
      );

      if (!isValidImages.every((isValid) => isValid)) {
        showError({
          title: 'Failed to upload moments',
          message:
            'One of the images you are attempting to upload does not meet the requirements: JPG or PNG up to 100MB'
        });
        setIsUploading(false);

        return;
      }

      const momentsToUpload = acceptedFiles.map((file) => ({
        tempId: uuid(),
        file
      }));
      const stateMoments = momentsToUpload.map((moment) => ({
        tempId: moment.tempId,
        filename: moment.file.name,
        isUploading: true
      }));

      setUploadedMoments((currentMoments) => [
        ...currentMoments,
        ...stateMoments
      ]);

      const chunks = _.chunk(momentsToUpload, UPLOAD_CHUNK_SIZE);

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        try {
          const uploadedImages = await Promise.all(
            chunk.map((moment) => uploadImage({ blob: moment.file }))
          );
          await createMoments({
            albumId: albumId || event?.album,
            moments: uploadedImages.map((image) => ({
              media: [
                {
                  type: 'image',
                  url: image.imageUrl
                }
              ],
              metadata: {
                uploadType: 'professional'
              }
            }))
          });
        } catch (error) {
          showError({
            title: 'Failed to upload moments',
            message: 'Please try again'
          });
        }

        // Mark moments as uploaded
        setUploadedMoments((currentMoments) =>
          currentMoments.map((moment) => {
            if (chunk.some((upload) => upload.tempId === moment.tempId)) {
              return { ...moment, isUploading: false };
            }
            return moment;
          })
        );
      }

      setIsUploading(false);
    },
    [event, uploadImage, createMoments]
  );

  const isLoading = isEventLoading || isUserLoading;

  if (isLoading) {
    return <></>;
  }

  return (
    <>
      {!hideHeader && (
        <Header
          title="Upload Moments"
          hasUser={!!user}
          onBack={() => router.back()}
        />
      )}

      <FadeIn>
        <div
          className={classNames('pb-20', {
            'md:mt-10': !albumId
          })}
        >
          <div className="px-4 md:px-0 md:w-[630px] md:mx-auto">
            {goBack && (
              <button
                type="button"
                onClick={goBack}
                className={classNames(styles.link, 'mt-5 mb-5')}
              >
                {t('moments.backToAlbum')}
              </button>
            )}
            <p className="text-[14px] text-gray-600 font-normal">
              {description}
            </p>

            <div className="mt-8">
              <FileUploader onDrop={handleOnDrop} isLoading={isUploading} />
            </div>

            {uploadedMoments.length === 0 ? (
              <div className="mt-24">
                <UploadedMomentsEmptyState />
              </div>
            ) : (
              <div className="mt-8">
                <UploadedMomentsList moments={uploadedMoments} />
              </div>
            )}
          </div>
        </div>
      </FadeIn>
    </>
  );
};

export default UploadMoments;
